import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { WebSocketProvider } from "@/contexts/WebSocketContext";
import { TenantProvider } from "@/contexts/TenantContext";
import NotificationCenter from "@/components/NotificationCenter";
import { PublicNotificationPopup } from "@/components/PublicNotifications";
import FeedbackButton from "@/components/FeedbackButton";

import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import AppSelector from "@/pages/AppSelector";
import OSINTApp from "@/pages/OSINTApp";
import MainLayout from "@/layouts/MainLayout";
import Dashboard from "@/pages/Dashboard";
import PoliciesPage from "@/pages/PoliciesPage";
import RisksPage from "@/pages/RisksPage";
import CompliancePage from "@/pages/CompliancePage";
import VendorsPage from "@/pages/VendorsPage";
import AlertsPage from "@/pages/AlertsPage";
import CloudLogsPage from "@/pages/CloudLogsPage";
import AuditTrailPage from "@/pages/AuditTrailPage";
import SettingsPage from "@/pages/SettingsPage";
import FrameworksPage from "@/pages/FrameworksPage";
import ControlsPage from "@/pages/ControlsPage";
import ControlDetailPage from "@/pages/ControlDetailPage";
import DocumentsPage from "@/pages/DocumentsPage";
import EvidencePage from "@/pages/EvidencePage";
import ExecutiveDashboard from "@/pages/ExecutiveDashboard";
import LiteSabreDashboard from "@/pages/LiteSabreDashboard";
import LiteSabreScan from "@/pages/LiteSabreScan";
import LiteSabreScans from "@/pages/LiteSabreScans";
import LiteSabreScanDetail from "@/pages/LiteSabreScanDetail";
import LiteSabreVulnerabilities from "@/pages/LiteSabreVulnerabilities";
import LiteSabreSCM from "@/pages/LiteSabreSCM";
import AmidalaDashboard from "@/pages/AmidalaDashboard";
import AmidalaAccounts from "@/pages/AmidalaAccounts";
import AmidalaScan from "@/pages/AmidalaScan";
import AmidalaScans from "@/pages/AmidalaScans";
import AmidalaConnectors from "@/pages/AmidalaConnectors";
import AmidalaScanDetail from "@/pages/AmidalaScanDetail";
import HealthDashboard from "@/pages/HealthDashboard";
import SBOMReportsPage from "@/pages/SBOMReportsPage";
import SBOMDetailPage from "@/pages/SBOMDetailPage";
import SBOMGeneratePage from "@/pages/SBOMGeneratePage";
import SupportRequestsPage from "@/pages/admin/SupportRequestsPage";
import TrainingAssignmentAdmin from "@/pages/admin/TrainingAssignmentAdmin";
import AdminDashboard from "@/pages/AdminDashboard";
import TenantDetailPage from "@/pages/TenantDetailPage";
import TenantAdminDashboard from "@/pages/TenantAdminDashboard";
import InviteAcceptPage from "@/pages/InviteAcceptPage";
import USPCFControlsPage from "@/pages/USPCFControlsPage";
import CrossFrameworkMapPage from "@/pages/CrossFrameworkMapPage";
import ComplianceDashboardPage from "@/pages/ComplianceDashboardPage";
import ComplianceTrendsDashboard from "@/pages/grc/ComplianceTrendsDashboard";

// Admin Pages
import SuperAdminsPage from "@/pages/admin/SuperAdminsPage";
import RolesPermissionsPage from "@/pages/admin/RolesPermissionsPage";
import LicenseTiersPage from "@/pages/admin/LicenseTiersPage";
import AuditLogsPage from "@/pages/admin/AuditLogsPage";
import VulnerabilityDBAdminPage from "@/pages/admin/VulnerabilityDBAdminPage";
import FrameworkBuilder from "@/pages/grc/FrameworkBuilder";
import YAMLBuilder from "@/pages/grc/YAMLBuilder";
import MarketingDemoPPT from "@/pages/grc/MarketingDemoPPT";
import { TrainingDashboard, TrainingModuleViewer } from "@/pages/TrainingPage";

// Risk Management Pages
import RiskDashboard from "@/pages/risk/RiskDashboard";
import RiskRegisterPage from "@/pages/risk/RiskRegisterPage";
import RiskAssessmentsPage from "@/pages/risk/RiskAssessmentsPage";
import RiskTreatmentsPage from "@/pages/risk/RiskTreatmentsPage";
import RiskKRIsPage from "@/pages/risk/RiskKRIsPage";

// Audit Management Pages
import AuditDashboard from "@/pages/audit/AuditDashboard";
import AuditSchedulePage from "@/pages/audit/AuditSchedulePage";
import AuditEngagementDetailPage from "@/pages/audit/AuditEngagementDetailPage";
import AuditEngagementsPage from "@/pages/audit/AuditEngagementsPage";
import ContinuousCompliancePage from "@/pages/grc/ContinuousCompliancePage";
import AuditFindingsPage from "@/pages/audit/AuditFindingsPage";
import AuditWorkpapersPage from "@/pages/audit/AuditWorkpapersPage";
import ScheduleAuditPage from "@/pages/audit/ScheduleAuditPage";
import AuditorReviewPage from "@/pages/audit/AuditorReviewPage";
import AuditInviteAcceptPage from "@/pages/audit/AuditInviteAcceptPage";

// Multi-Cloud Catalog & Scanner Pages
import MultiCloudCatalogPage from "@/pages/catalog/MultiCloudCatalogPage";
import StackProfilesPage from "@/pages/scanner/StackProfilesPage";
import LiteSabreProPage from "@/pages/scanner/LiteSabreProPage";
import LiteSabreProScanDetailPage from "@/pages/scanner/LiteSabreProScanDetailPage";
import AIPentestSessionPage from "@/pages/scanner/AIPentestSessionPage";
import IntegrationsPage from "@/pages/scanner/IntegrationsPage";
import CICDPipelinePage from "@/pages/scanner/CICDPipelinePage";
import ScanTrendAnalysis from "@/pages/ScanTrendAnalysis";
import GitHubScanPage from "@/pages/scanner/GitHubScanPage";
import ThreatModelingPage from "@/pages/scanner/ThreatModelingPage";
import MobileAnalysisPage from "@/pages/MobileAnalysisPage";
import PolicyTemplatesPage from "@/pages/grc/PolicyTemplatesPage";
import PolicyCreatePage from "@/pages/grc/PolicyCreatePage";
import VendorAssessmentPage from "@/pages/grc/VendorAssessmentPage";
import MyCompliancePage from "@/pages/MyCompliancePage";
import TrustCenterAdmin from "@/pages/grc/TrustCenterAdmin";
import PublicTrustCenter from "@/pages/public/PublicTrustCenter";
import AutoEvidencePage from "@/pages/grc/AutoEvidencePage";

// DPDPA Consent Manager Pages
import DPDPADashboard from "@/pages/dpdpa/DPDPADashboard";
import ConsentPurposesPage from "@/pages/dpdpa/ConsentPurposesPage";
import ConsentsPage from "@/pages/dpdpa/ConsentsPage";
import DSRPage from "@/pages/dpdpa/DSRPage";
import PIIScannerPage from "@/pages/dpdpa/PIIScannerPage";
import AlertRulesPage from "@/pages/dpdpa/AlertRulesPage";
import DPDPAAuditTrailPage from "@/pages/dpdpa/DPDPAAuditTrailPage";
import DBConnectionsPage from "@/pages/dpdpa/DBConnectionsPage";
import ConsentBannerPage from "@/pages/dpdpa/ConsentBannerPage";
import BreachTrackerPage from "@/pages/dpdpa/BreachTrackerPage";
import PreAssessmentPage from "@/pages/dpdpa/PreAssessmentPage";
import BreachDetectionPage from "@/pages/dpdpa/BreachDetectionPage";
import LegacyMigrationPage from "@/pages/dpdpa/LegacyMigrationPage";
import ResidencyNISTPage from "@/pages/dpdpa/ResidencyNISTPage";
import DataFlowPage from "@/pages/dpdpa/DataFlowPage";
import DLPConnectorPage from "@/pages/dpdpa/DLPConnectorPage";

const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  const user = localStorage.getItem('user');
  return (token || user) ? children : <Navigate to="/login" replace />;
};

// Router component to handle SSO callback detection
const AppRouter = () => {
  const location = useLocation();

  // Subdomain-based Trust Center: trust.{custom-slug}.{root-domain}
  // e.g. trust.beskarit.com -> render PublicTrustCenter for slug "beskarit"
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const trustSubdomainMatch = /^trust\.([a-z0-9][a-z0-9-]{1,60})\./i.exec(host);
  if (trustSubdomainMatch) {
    return <PublicTrustCenter subdomainSlug={trustSubdomainMatch[1]} />;
  }
  // Custom-domain Trust Center: the entire hostname is a bring-your-own-domain
  // (e.g. trust.acme.com). We defer to PublicTrustCenter which will resolve the tenant via /by-host/lookup.
  if (host && !host.includes('localhost') && !/preview\.emergentagent\.com$/.test(host) && !/beskarit\.com$/.test(host)) {
    return <PublicTrustCenter customHost={host} />;
  }

  // Check for session_id in URL fragment (SSO callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<AuthPage />} />
      <Route path="/" element={<Navigate to="/apps" replace />} />
      
      {/* Invitation Accept Page - Public route */}
      <Route path="/invite/accept" element={<InviteAcceptPage />} />
      
      {/* Public Trust Center - No auth */}
      <Route path="/trust/:slug" element={<PublicTrustCenter />} />
      <Route path="/trust/:slug/private" element={<PublicTrustCenter />} />
      
      {/* Audit Invitation Accept Page - Public route (supports logged in or new users) */}
      <Route path="/audit-invite/:token" element={<AuditInviteAcceptPage />} />
      
      {/* Training - Redirect to nested route */}
      <Route path="/training" element={<Navigate to="/grc/training" replace />} />
      <Route path="/training/:code" element={<Navigate to="/grc/training" replace />} />

      {/* App Selector - Choose between GRC and OSINT */}
      <Route
        path="/apps"
        element={
          <PrivateRoute>
            <AppSelector />
          </PrivateRoute>
        }
      />
      
      {/* Marketing Demo - Standalone at landing page level */}
      <Route
        path="/marketing-demo"
        element={
          <PrivateRoute>
            <MarketingDemoPPT />
          </PrivateRoute>
        }
      />

      {/* OSINT Toolkit Routes */}
      <Route
        path="/osint/*"
        element={
          <PrivateRoute>
            <OSINTApp />
          </PrivateRoute>
        }
      />
      
      {/* Amidala CSPM Routes - Standalone */}
      <Route
        path="/amidala"
        element={
          <PrivateRoute>
            <MainLayout appContext="amidala" />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="/amidala/dashboard" replace />} />
        <Route path="dashboard" element={<AmidalaDashboard />} />
        <Route path="accounts" element={<AmidalaAccounts />} />
        <Route path="accounts/:accountId" element={<AmidalaAccounts />} />
        <Route path="scan" element={<AmidalaScan />} />
        <Route path="scans" element={<AmidalaScans />} />
        <Route path="scans/:scanId" element={<AmidalaScanDetail />} />
        <Route path="connectors" element={<AmidalaConnectors />} />
        <Route path="catalog" element={<MultiCloudCatalogPage />} />
      </Route>
      
      {/* LiteSabre Scanner Routes - Standalone */}
      <Route
        path="/litesabre"
        element={
          <PrivateRoute>
            <MainLayout appContext="litesabre" />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="/litesabre/dashboard" replace />} />
        <Route path="dashboard" element={<LiteSabreDashboard />} />
        <Route path="scan" element={<LiteSabreScan />} />
        <Route path="scans" element={<LiteSabreScans />} />
        <Route path="scans/:scanId" element={<LiteSabreScanDetail />} />
        <Route path="trends" element={<ScanTrendAnalysis />} />
        <Route path="vulnerabilities" element={<LiteSabreVulnerabilities />} />
        <Route path="vulnerabilities/:vulnId" element={<LiteSabreVulnerabilities />} />
        <Route path="sbom" element={<SBOMReportsPage />} />
        <Route path="sbom/new" element={<SBOMGeneratePage />} />
        <Route path="sbom/:sbomId" element={<SBOMDetailPage />} />
        <Route path="scm" element={<LiteSabreSCM />} />
      </Route>
      
      {/* LiteSabre Pro Routes - Standalone */}
      <Route
        path="/litesabre-pro"
        element={
          <PrivateRoute>
            <MainLayout appContext="litesabre_pro" />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="/litesabre-pro/dashboard" replace />} />
        <Route path="dashboard" element={<LiteSabreProPage />} />
        <Route path="scans/:scanId" element={<LiteSabreProScanDetailPage />} />
        <Route path="pentest/:sessionId" element={<AIPentestSessionPage />} />
        <Route path="stack-profiles" element={<StackProfilesPage />} />
        <Route path="cicd-pipelines" element={<CICDPipelinePage />} />
        <Route path="github-scanning" element={<GitHubScanPage />} />
        <Route path="mobile-analysis" element={<MobileAnalysisPage />} />
        <Route path="integrations" element={<IntegrationsPage />} />
        <Route path="ThreatModelling" element={<ThreatModelingPage />} />
      </Route>
      
      {/* GRC Nexus Routes */}
      <Route
        path="/grc"
        element={
          <PrivateRoute>
            <MainLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="/grc/executive-dashboard" replace />} />
        <Route path="dashboard" element={<Navigate to="/grc/executive-dashboard" replace />} />
        <Route path="executive-dashboard" element={<ExecutiveDashboard />} />
        <Route path="compliance-dashboard" element={<ComplianceDashboardPage />} />
        <Route path="compliance-trends" element={<ComplianceTrendsDashboard />} />
        <Route path="framework-builder" element={<FrameworkBuilder />} />
        <Route path="yaml-builder" element={<YAMLBuilder />} />
        <Route path="marketing-demo" element={<Navigate to="/apps" replace />} />
        <Route path="frameworks" element={<FrameworksPage />} />
        <Route path="uspcf" element={<USPCFControlsPage />} />
        <Route path="cross-framework-map" element={<CrossFrameworkMapPage />} />
        <Route path="controls" element={<ControlsPage />} />
        <Route path="controls/:controlId" element={<ControlDetailPage />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="evidence" element={<EvidencePage />} />
        <Route path="litesabre" element={<LiteSabreDashboard />} />
        <Route path="litesabre/scan" element={<LiteSabreScan />} />
        <Route path="litesabre/scans" element={<LiteSabreScans />} />
        <Route path="litesabre/scans/:scanId" element={<LiteSabreScanDetail />} />
        <Route path="litesabre/trends" element={<ScanTrendAnalysis />} />
        <Route path="litesabre/vulnerabilities" element={<LiteSabreVulnerabilities />} />
        <Route path="litesabre/vulnerabilities/:vulnId" element={<LiteSabreVulnerabilities />} />
        <Route path="litesabre/sbom" element={<SBOMReportsPage />} />
        <Route path="litesabre/sbom/new" element={<SBOMGeneratePage />} />
        <Route path="litesabre/sbom/:sbomId" element={<SBOMDetailPage />} />
        <Route path="amidala" element={<AmidalaDashboard />} />
        <Route path="amidala/accounts" element={<AmidalaAccounts />} />
        <Route path="amidala/accounts/:accountId" element={<AmidalaAccounts />} />
        <Route path="amidala/scan" element={<AmidalaScan />} />
        <Route path="amidala/scans" element={<AmidalaScans />} />
        <Route path="amidala/scans/:scanId" element={<AmidalaScanDetail />} />
        <Route path="amidala/connectors" element={<AmidalaConnectors />} />
        <Route path="health" element={<HealthDashboard />} />
        <Route path="training" element={<TrainingDashboard />} />
        <Route path="training/:code" element={<TrainingModuleViewer />} />
        <Route path="policies" element={<PoliciesPage />} />
        <Route path="risks" element={<RisksPage />} />
        <Route path="compliance" element={<CompliancePage />} />
        <Route path="vendors" element={<VendorsPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="cloud-logs" element={<CloudLogsPage />} />
        <Route path="audit-trail" element={<AuditTrailPage />} />
        <Route path="settings" element={<SettingsPage />} />
        
        {/* Risk Management Routes */}
        <Route path="risk" element={<RiskDashboard />} />
        <Route path="risk/register" element={<RiskRegisterPage />} />
        <Route path="risk/register/:riskId" element={<RiskRegisterPage />} />
        <Route path="risk/assessments" element={<RiskAssessmentsPage />} />
        <Route path="risk/treatments" element={<RiskTreatmentsPage />} />
        <Route path="risk/kris" element={<RiskKRIsPage />} />
        <Route path="risk/appetite" element={<RiskDashboard />} />
        
        {/* Audit Management Routes */}
        <Route path="audit" element={<AuditDashboard />} />
        <Route path="audit/schedule" element={<ScheduleAuditPage />} />
        <Route path="audit/scheduled/:auditId/review" element={<AuditorReviewPage />} />
        <Route path="audit/engagements" element={<AuditEngagementsPage />} />
        <Route path="audit/engagements/:engagementId" element={<AuditEngagementDetailPage />} />
        <Route path="audit/findings" element={<AuditFindingsPage />} />
        <Route path="audit/findings/:findingId" element={<AuditFindingsPage />} />
        <Route path="audit/workpapers" element={<AuditWorkpapersPage />} />
        <Route path="audit/reports" element={<AuditDashboard />} />
        
        {/* Multi-Cloud Control Catalog */}
        <Route path="catalog" element={<MultiCloudCatalogPage />} />
        
        {/* LiteSabre Pro Scanner */}
        <Route path="litesabre-pro" element={<LiteSabreProPage />} />
        <Route path="litesabre-pro/scans/:scanId" element={<LiteSabreProScanDetailPage />} />
        
        {/* Integrations - Redirect to Settings with integrations tab */}
        <Route path="integrations" element={<Navigate to="/grc/settings?tab=integrations" replace />} />
        
        {/* CI/CD Pipeline */}
        <Route path="cicd-pipelines" element={<CICDPipelinePage />} />
        
        {/* GitHub Scanning */}
        <Route path="github-scanning" element={<GitHubScanPage />} />
        
        {/* Mobile Dynamic Analysis */}
        <Route path="mobile-analysis" element={<MobileAnalysisPage />} />
        
        {/* Stack Profiles */}
        <Route path="stack-profiles" element={<StackProfilesPage />} />
        
        {/* Policy Templates */}
        <Route path="policy-templates" element={<PolicyTemplatesPage />} />
        <Route path="policy-templates/create" element={<PolicyCreatePage />} />
        <Route path="policy-templates/view/:policyId" element={<PolicyTemplatesPage />} />

        {/* My Compliance (User self-serve training + policy ack) */}
        <Route path="my-compliance" element={<MyCompliancePage />} />

        {/* Trust Center (Tenant Admin / Trust Admin) */}
        <Route path="trust-center" element={<TrustCenterAdmin />} />

        {/* Auto Evidence — control coverage aggregated from every source */}
        <Route path="auto-evidence" element={<AutoEvidencePage />} />
        
        {/* Vendor Risk Assessment */}
        <Route path="vendor-assessment" element={<VendorAssessmentPage />} />
        <Route path="vendor-assessment/vendors" element={<VendorAssessmentPage />} />
        <Route path="vendor-assessment/vendors/:vendorId" element={<VendorAssessmentPage />} />
        <Route path="vendor-assessment/assessments" element={<VendorAssessmentPage />} />
        <Route path="vendor-assessment/assessments/:assessmentId" element={<VendorAssessmentPage />} />
        <Route path="vendor-assessment/questionnaires" element={<VendorAssessmentPage />} />
        
        {/* DPDPA Consent Manager */}
        <Route path="dpdpa" element={<DPDPADashboard />} />

        {/* Training Admin (moved from /admin) */}
        <Route path="training-admin" element={<TrainingAssignmentAdmin />} />

        {/* Continuous Compliance */}
        <Route path="continuous-compliance" element={<ContinuousCompliancePage />} />

        <Route path="dpdpa/purposes" element={<ConsentPurposesPage />} />
        <Route path="dpdpa/consents" element={<ConsentsPage />} />
        <Route path="dpdpa/requests" element={<DSRPage />} />
        <Route path="dpdpa/pii-scanner" element={<PIIScannerPage />} />
        <Route path="dpdpa/db-connections" element={<DBConnectionsPage />} />
        <Route path="dpdpa/banners" element={<ConsentBannerPage />} />
        <Route path="dpdpa/breaches" element={<BreachTrackerPage />} />
        <Route path="dpdpa/pre-assessment" element={<PreAssessmentPage />} />
        <Route path="dpdpa/alert-rules" element={<AlertRulesPage />} />
        <Route path="dpdpa/audit" element={<DPDPAAuditTrailPage />} />
        <Route path="dpdpa/breach-detection" element={<BreachDetectionPage />} />
        <Route path="dpdpa/migration" element={<LegacyMigrationPage />} />
        <Route path="dpdpa/residency-nist" element={<ResidencyNISTPage />} />
        <Route path="dpdpa/data-flow" element={<DataFlowPage />} />
        <Route path="dpdpa/dlp-connector" element={<DLPConnectorPage />} />
      </Route>
      
      {/* Admin Portal Routes - Using MainLayout for consistent navigation */}
      <Route
        path="/admin"
        element={
          <PrivateRoute>
            <MainLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="dashboard" element={<Navigate to="/admin" replace />} />
        <Route path="tenants/:tenantId" element={<TenantDetailPage />} />
        <Route path="tenants/:tenantId/members" element={<TenantDetailPage />} />
        <Route path="my-org" element={<TenantAdminDashboard />} />
        <Route path="super-admins" element={<SuperAdminsPage />} />
        <Route path="roles" element={<RolesPermissionsPage />} />
        <Route path="licenses" element={<LicenseTiersPage />} />
        <Route path="audit-logs" element={<AuditLogsPage />} />
        <Route path="vulnerability-db" element={<VulnerabilityDBAdminPage />} />
        <Route path="support-requests" element={<SupportRequestsPage />} />
        <Route path="training-admin" element={<Navigate to="/grc/training-admin" replace />} />
      </Route>
      
      {/* Legacy routes redirect to /grc */}
      <Route path="/dashboard" element={<Navigate to="/grc/executive-dashboard" replace />} />
      <Route path="/policies" element={<Navigate to="/grc/policies" replace />} />
      <Route path="/risks" element={<Navigate to="/grc/risks" replace />} />
      <Route path="/compliance" element={<Navigate to="/grc/compliance" replace />} />
      <Route path="/vendors" element={<Navigate to="/grc/vendors" replace />} />
      <Route path="/alerts" element={<Navigate to="/grc/alerts" replace />} />
      <Route path="/cloud-logs" element={<Navigate to="/grc/cloud-logs" replace />} />
      <Route path="/audit-trail" element={<Navigate to="/grc/audit-trail" replace />} />
      <Route path="/settings" element={<Navigate to="/grc/settings" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <WebSocketProvider>
        <TenantProvider>
          <BrowserRouter>
            <AppRouter />
            <NotificationCenter position="top-right" maxVisible={5} />
            <PublicNotificationPopup />
            <FeedbackButton />
          </BrowserRouter>
          <Toaster position="top-right" />
        </TenantProvider>
      </WebSocketProvider>
    </ThemeProvider>
  );
}

export default App;
